import { Component, DestroyRef, EventEmitter, inject, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NZ_DRAWER_DATA, NzDrawerRef } from 'ng-zorro-antd/drawer';
import { Company, Currency, Partner, ToastConfig, User } from '@app/shared';
import { DrawerEscapeBlockerService, getSafeImageUrl } from '@app/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { GlobalValuesService } from '@app/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../../../core/services/auth.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { CompanyService } from '@app/core';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-convert-lead-drawer',
  standalone: false,
  templateUrl: './convert-lead-drawer.component.html',
  styleUrl: './convert-lead-drawer.component.less',
})
export class ConvertLeadDrawerComponent implements OnInit, OnDestroy {
  nzData: {
    leadId: number;
    lead?: any; // Objeto completo del Lead para acceder a teamId y userId
  } = inject(NZ_DRAWER_DATA);

  convertForm!: FormGroup;
  converting: boolean = false;
  notRef: any;
  @ViewChild('notificationTmpl') notificationTmpl: any;
  toastConfig: ToastConfig = {
    icon: 'check_circle',
    iconColor: '#6CDA75',
    content: 'Lead convertido exitosamente.',
    showUndo: false,
  };
  @Output() convertEvent = new EventEmitter<{
    opportunityType: string;
    userId: number|null;
    teamId: number;
    costCenterId: number;
    currencyId: number;
    expectedClosingDate: string | null;
  }>();
  protected readonly getSafeImageUrl = getSafeImageUrl;

  companiesList: Company[] = [];
  usersList: User[] = [];
  commercialList: Partner[] = [];
  filteredCommercialList: Partner[] = [];
  costCenterList: { id: number; name: string }[] = [];
  salesTeamList: { id: number; name: string; memberList?: any[] }[] = [];
  currencyList: Currency[] = [];
  hasTeamMembers: boolean = true;

  private destroyRef = inject(DestroyRef);

  constructor(
    private drawerRef: NzDrawerRef<string>,
    private notificationService: NzNotificationService,
    private fb: FormBuilder,
    private globalValuesService: GlobalValuesService,
    private authService: AuthService,
    private drawerEscapeBlocker: DrawerEscapeBlockerService,
    private http: HttpClient,
    private companyService: CompanyService
  ) {
    // Load companies
    this.globalValuesService.companies$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((companies: Company[]) => {
        this.companiesList = companies;
      });

    // Load users
    this.globalValuesService.users$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((users: User[]) => {
        this.usersList = users;
        // Mapear usuarios a partners para usar con avatar-select
        this.commercialList = users
          .filter(user => user.partner)
          .map(user => user.partner);
        // Inicializar filteredCommercialList como vacío hasta que se seleccione un equipo
        this.filteredCommercialList = [];

        // Si hay un equipo seleccionado, filtrar los comerciales
        const selectedTeamId = this.convertForm?.get('salesTeam')?.value;
        if (selectedTeamId) {
          this.filterCommercialsByTeam(selectedTeamId);
        }

        // Si hay un lead con comercial, asegurarse de que esté en la lista filtrada
        const lead = this.nzData?.lead;
        if (lead?.userId && selectedTeamId) {
          const leadUser = users.find(u => u.id === lead.userId);
          if (leadUser?.partner?.id) {
            // Verificar si el comercial está en la lista filtrada
            const commercialExists = this.filteredCommercialList.some(c => c.id === leadUser.partner.id);
            if (commercialExists && this.convertForm) {
              // Si el formulario ya existe y el comercial no está establecido, establecerlo
              const currentCommercial = this.convertForm.get('commercial')?.value;
              if (!currentCommercial) {
                this.convertForm.patchValue({ commercial: leadUser.partner.id });
              }
            }
          }
        }
      });

    // Load cost centers
    this.globalValuesService.costCenters$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((costCenters: { id: number; name: string }[]) => {
        this.costCenterList = costCenters;
      });

    // Load sales teams
    this.globalValuesService.salesTeams$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((salesTeams: { id: number; name: string; memberList?: any[] }[]) => {
        this.salesTeamList = salesTeams;
        // Si hay un equipo seleccionado, actualizar la lista de comerciales
        const selectedTeamId = this.convertForm?.get('salesTeam')?.value;
        if (selectedTeamId) {
          this.filterCommercialsByTeam(selectedTeamId);
        }
      });

    // Load currencies
    this.globalValuesService.currencies$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((currencies: Currency[]) => {
        this.currencyList = currencies || [];
      });
  }

  ngOnInit(): void {
    this.initForm();

    // Suscribirse a cambios en salesTeam para filtrar comerciales
    this.convertForm
      .get('salesTeam')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((teamId: number | null) => {
        // Cuando el usuario cambia el equipo manualmente, limpiar el comercial
        this.onSalesTeamChange(teamId, false);
      });

    // Pre-cargar equipo, comercial y centro de costo desde el lead
    const lead = this.nzData?.lead;
    if (lead) {
      if (lead.costCenterId && !this.convertForm.get('costCenter')?.value) {
        this.convertForm.patchValue({ costCenter: lead.costCenterId }, { emitEvent: false });
      }
      if (lead.teamId) {
        setTimeout(() => {
          if (lead.teamId && this.salesTeamList.length > 0 && this.usersList.length > 0) {
            this.convertForm.patchValue({ salesTeam: lead.teamId }, { emitEvent: false });
            this.onSalesTeamChange(lead.teamId, true);
          }
        }, 300);
      }
    }

    this.drawerEscapeBlocker.block();
  }

  initForm(): void {
    const lead = this.nzData?.lead;
    const defaultTeamId = lead?.teamId ?? null;
    const defaultCostCenterId = lead?.costCenterId ?? null;
    const defaultCommercialId = null;

    this.convertForm = this.fb.group({
      opportunityType: [null, Validators.required],
      salesTeam: [defaultTeamId, Validators.required],
      commercial: [{ value: defaultCommercialId, disabled: !defaultTeamId }, Validators.required],
      costCenter: [defaultCostCenterId, Validators.required],
      currencyId: [null, Validators.required],
      expectedClosingDate: [null, Validators.required],
    });
  }

  close(): void {
    this.drawerRef.close();
  }

  discard(): void {
    this.close();
  }

  convert(): void {
    if (this.convertForm.invalid) {
      // Marcar todos los campos como touched para mostrar errores
      Object.keys(this.convertForm.controls).forEach((key) => {
        this.convertForm.get(key)?.markAsTouched();
        this.convertForm.get(key)?.updateValueAndValidity();
      });
      return;
    }

    const formValue = this.convertForm.value;

    // Mapear el tipo de oportunidad al formato del backend
    let opportunityTypeValue: string | null = null;
    if (formValue.opportunityType === 'Proyecto') {
      opportunityTypeValue = 'project';
    } else if (formValue.opportunityType === 'Servicio Puntual') {
      opportunityTypeValue = 'punctualservice';
    }

    // Obtener el userId del comercial (partnerId)
    const commercialUserId = formValue.commercial
      ? this.usersList.find((u) => u.partner?.id === formValue.commercial)
          ?.id || null
      : null;

    // Formatear la fecha de cierre previsto si existe
    const expectedClosingDate = formValue.expectedClosingDate
      ? new Date(formValue.expectedClosingDate).toISOString()
      : null;

    // Emitir evento con los datos
    this.convertEvent.emit({
      opportunityType: opportunityTypeValue || '',
      userId: commercialUserId || null,
      teamId: formValue.salesTeam || null,
      costCenterId: formValue.costCenter || null,
      currencyId: formValue.currencyId || null,
      expectedClosingDate: expectedClosingDate,
    });

    // Cerrar el drawer
    this.close();
  }

  showNotification(
    placement:
      | 'topLeft'
      | 'topRight'
      | 'bottomLeft'
      | 'bottomRight' = 'bottomRight',
  ): void {
    this.notRef = this.notificationService.create(
      'blank',
      '',
      this.notificationTmpl,
      { nzPlacement: placement },
    );
  }

  // Método para manejar cambios en el equipo de ventas
  onSalesTeamChange(teamId: number | null, preserveCommercial: boolean = false): void {
    if (!teamId) {
      // Si no hay equipo seleccionado, limpiar la lista y deshabilitar el campo comercial
      this.filteredCommercialList = [];
      this.hasTeamMembers = false;
      this.convertForm.get('commercial')?.setValue(null);
      this.convertForm.get('commercial')?.disable();
      return;
    }

    // Solo limpiar el comercial si no se debe preservar
    if (!preserveCommercial) {
      this.convertForm.get('commercial')?.setValue(null);
    }

    // Habilitar el campo comercial
    this.convertForm.get('commercial')?.enable();

    // Verificar si necesitamos cargar los datos completos del equipo
    const selectedTeam = this.salesTeamList.find((team) => team.id === teamId);

    if (
      !selectedTeam ||
      !selectedTeam.memberList ||
      selectedTeam.memberList.length === 0
    ) {
      // Si el equipo no tiene memberList, intentar cargar los datos completos
      this.loadSalesTeamDetails(teamId, preserveCommercial);
    } else {
      // Si ya tiene memberList, filtrar directamente
      this.filterCommercialsByTeam(teamId);
      // Si se debe preservar el comercial, establecerlo después de filtrar
      if (preserveCommercial) {
        this.setCommercialFromLead();
      }
    }
  }

  // Método auxiliar para establecer el comercial desde el lead
  private setCommercialFromLead(): void {
    const lead = this.nzData?.lead;
    if (lead?.userId && this.usersList.length > 0) {
      const leadUser = this.usersList.find(u => u.id === lead.userId);
      if (leadUser?.partner?.id) {
        // Verificar que el comercial pertenezca al equipo filtrado
        const commercialExists = this.filteredCommercialList.some(c => c.id === leadUser.partner.id);
        if (commercialExists) {
          // Establecer el comercial - esto hará que se muestre en el avatar-select
          this.convertForm.patchValue({ commercial: leadUser.partner.id });
        }
      }
    }
  }

  // Cargar los miembros del equipo de ventas usando el endpoint salesteammember
  loadSalesTeamDetails(teamId: number, preserveCommercial: boolean = false): void {
    const url = `${environment.coreApiBaseUrl}/api/salesteammember`;
    const params = new HttpParams()
      .set('teamId', teamId.toString())
      .set('startRow', '0')
      .set('endRow', '100')
      .set('sortDirection', 'desc')
      .set('sortProperty', 'id');

    const headers = this.companyService.getCompanyHeaders();

    this.http
      .get<any>(url, { params, headers })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response?.data?.rows && response.data.rows.length > 0) {
            // Convertir los miembros al formato memberList que espera el código
            const memberList = response.data.rows.map((member: any) => ({
              userId: member.userId,
              id: member.userId,
              role: member.role,
              isActive: member.isActive,
            }));

            // Actualizar el equipo en la lista local con los miembros
            const teamIndex = this.salesTeamList.findIndex(
              (t) => t.id === teamId
            );
            if (teamIndex !== -1) {
              this.salesTeamList[teamIndex] = {
                ...this.salesTeamList[teamIndex],
                memberList: memberList,
                members: response.data.rows,
              } as any;
            } else {
              // Si no existe, agregarlo
              const teamData: any = {
                id: teamId,
                memberList: memberList,
                members: response.data.rows,
              };
              this.salesTeamList.push(teamData);
            }

            // Filtrar los comerciales con los miembros cargados
            this.hasTeamMembers = true;
            this.filterCommercialsByTeam(teamId);

            // Si se debe preservar el comercial, establecerlo después de filtrar
            if (preserveCommercial) {
              setTimeout(() => {
                this.setCommercialFromLead();
              }, 100);
            }
          } else {
            // Si no hay miembros, dejar la lista vacía y deshabilitar el campo
            this.filteredCommercialList = [];
            this.hasTeamMembers = false;
            this.convertForm.get('commercial')?.setValue(null);
            this.convertForm.get('commercial')?.disable();
          }
        },
        error: (error) => {
          console.error('Error al cargar miembros del equipo:', error);
          // Si falla, intentar filtrar con los datos disponibles
          this.filterCommercialsByTeam(teamId);
          if (preserveCommercial) {
            setTimeout(() => {
              this.setCommercialFromLead();
            }, 100);
          }
        },
      });
  }

  // Método auxiliar para filtrar comerciales por equipo
  filterCommercialsByTeam(teamId: number): void {
    let selectedTeam = this.salesTeamList.find((team) => team.id === teamId);

    // Si no se encuentra en la lista local, intentar buscarlo en globalValuesService
    if (!selectedTeam) {
      const teamsFromGlobal = this.globalValuesService.salesTeams$.value;
      selectedTeam = teamsFromGlobal.find((team) => team.id === teamId);
      if (selectedTeam) {
        this.salesTeamList.push(selectedTeam as any);
      }
    }

    if (!selectedTeam) {
      console.warn('No se encontró el equipo con id:', teamId);
      this.filteredCommercialList = [];
      this.hasTeamMembers = false;
      this.convertForm.get('commercial')?.setValue(null);
      this.convertForm.get('commercial')?.disable();
      return;
    }

    // Si el equipo no tiene memberList, no hay miembros disponibles
    if (!selectedTeam.memberList || selectedTeam.memberList.length === 0) {
      this.filteredCommercialList = [];
      this.hasTeamMembers = false;
      this.convertForm.get('commercial')?.setValue(null);
      this.convertForm.get('commercial')?.disable();
      return;
    }

    // Filtrar los comerciales que pertenecen al equipo
    const teamMemberIds = selectedTeam.memberList.map((member: any) => member.userId || member.id);
    const filtered = this.commercialList.filter((partner) => {
      const user = this.usersList.find((u) => u.partner?.id === partner.id);
      return user && teamMemberIds.includes(user.id);
    });

    this.filteredCommercialList = [...filtered];
    this.hasTeamMembers = filtered.length > 0;

    // Verificar si el comercial actual pertenece al equipo filtrado
    const currentCommercial = this.convertForm.get('commercial')?.value;
    if (currentCommercial) {
      const commercialExists = filtered.some(c => c.id === currentCommercial);
      if (!commercialExists) {
        // Si el comercial actual no pertenece al equipo, limpiarlo
        this.convertForm.get('commercial')?.setValue(null);
      }
    }

    // Si no hay comerciales disponibles, deshabilitar el campo
    if (filtered.length === 0) {
      this.convertForm.get('commercial')?.disable();
    } else {
      this.convertForm.get('commercial')?.enable();
    }
  }

  ngOnDestroy(): void {
    this.drawerEscapeBlocker.unblock();
  }

  // Función para deshabilitar fechas anteriores a hoy
  disabledPastDate = (current: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const currentDate = new Date(current);
    currentDate.setHours(0, 0, 0, 0);
    // Retorna true si la fecha es anterior a hoy (debe estar deshabilitada)
    return currentDate < today;
  };
}

